import { useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Download } from "lucide-react";
import { ledgersApi, type PoolLedgerRow } from "@/api/ledgers";
import { spvPoolsApi } from "@/api/spvPools";
import { Badge, Button, Card, Input } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import {
  downloadCsv,
  formatDate,
  formatMoney,
  formatPct,
} from "./ledgerHelpers";

const OC_THRESHOLD = 1.05; // Design spec §9 — illustrative threshold line.

/**
 * Payment System v3.0 PR 11 — Pool Ledger (design spec §10.7). All WF2
 * disbursements over time, grouped into the closing-event (1-8) and
 * operating-revenue (9-41) sections, with the OC trigger gauge + admin
 * toggle and the pool-vs-loans reconciliation panel.
 */
export function PoolLedgerPage() {
  const { id: poolId } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const canToggleOc = hasPermission("SpvPool.Build");

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const ledger = useQuery({
    queryKey: ["ledger.pool", poolId, from, to],
    queryFn: () =>
      ledgersApi.pool(poolId!, {
        from: from || undefined,
        to: to || undefined,
      }),
    enabled: !!poolId,
  });

  const setOc = useMutation({
    mutationFn: (active: boolean) => spvPoolsApi.setOcTrigger(poolId!, active),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ledger.pool", poolId] }),
  });

  const data = ledger.data;

  const columns: Column<PoolLedgerRow>[] = [
    { key: "date", header: "Date", render: (r) => formatDate(r.date) },
    { key: "priority", header: "Priority", render: (r) => r.priorityPosition },
    {
      key: "obligation",
      header: "Obligation Type",
      render: (r) => r.obligationTypeDisplay ?? r.obligationTypeCode,
    },
    { key: "recipient", header: "Recipient", render: (r) => r.recipient ?? "—" },
    { key: "loanId", header: "Loan", render: (r) => r.loanId ?? "—" },
    { key: "amount", header: "Amount", render: (r) => formatMoney(r.amount) },
    {
      key: "status",
      header: "Status",
      render: (r) => <Badge status={r.status === "Reversed" ? "error" : "info"}>{r.status}</Badge>,
    },
  ];

  function exportCsv() {
    if (!data) return;
    const rows = data.sections.flatMap((s) =>
      s.rows.map((r) => [
        s.sectionName,
        r.date,
        r.priorityPosition,
        r.obligationTypeCode,
        r.recipient ?? "",
        r.loanId ?? "",
        r.amount,
        r.status,
      ]),
    );
    downloadCsv(
      `pool-ledger-${data.poolName ?? data.poolId}.csv`,
      ["Section", "Date", "Priority", "Obligation Type", "Recipient", "Loan", "Amount", "Status"],
      rows,
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Ledger"
        title={`Pool Ledger — ${data?.poolName ?? poolId}`}
        description="All Waterfall 2 disbursements posted against this pool, grouped by section. The OC trigger gauge sits at the top; admins with SpvPool.Build can flip the trigger flag."
        actions={
          <Button onClick={exportCsv} disabled={!data?.sections.length}>
            <Download size={16} aria-hidden /> Export CSV
          </Button>
        }
      />

      {data && (
        <Card className="mb-space-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-space-4 items-start">
            <OcGauge
              ratio={data.ocRatio}
              threshold={OC_THRESHOLD}
              active={data.ocTriggerActive}
            />
            <div>
              <div className="text-caption uppercase text-gray-mid mb-space-1">OC Trigger</div>
              <div className="flex items-center gap-space-2">
                <Badge status={data.ocTriggerActive ? "error" : "finalized"}>
                  {data.ocTriggerActive ? "ACTIVE" : "Inactive"}
                </Badge>
                {data.ocTriggerActive && (
                  <span className="inline-flex items-center gap-space-1 text-small text-status-error">
                    <AlertTriangle size={14} aria-hidden />
                    skip_if_oc_trigger priorities will be skipped
                  </span>
                )}
              </div>
              {canToggleOc && (
                <div className="mt-space-2 flex gap-space-2">
                  <Button
                    onClick={() => setOc.mutate(true)}
                    disabled={data.ocTriggerActive || setOc.isPending}
                  >
                    Set Trigger
                  </Button>
                  <Button
                    onClick={() => setOc.mutate(false)}
                    disabled={!data.ocTriggerActive || setOc.isPending}
                  >
                    Clear Trigger
                  </Button>
                </div>
              )}
            </div>
            <ReconciliationPanel
              poolTotal={data.reconciliation.poolLedgerTotal}
              loansTotal={data.reconciliation.contributingLoansWf2Total}
              variance={data.reconciliation.variance}
            />
          </div>
        </Card>
      )}

      <Card className="mb-space-4">
        <div className="grid grid-cols-2 gap-space-3">
          <label className="flex flex-col gap-space-1 text-small">
            <span className="text-caption uppercase">From</span>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="flex flex-col gap-space-1 text-small">
            <span className="text-caption uppercase">To</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
      </Card>

      {ledger.isLoading && (
        <div className="text-center py-space-6 text-caption uppercase">Loading…</div>
      )}
      {ledger.isError && (
        <div className="text-status-error">
          {(ledger.error as Error).message}
        </div>
      )}

      {data?.sections.map((section) => (
        <Card key={section.sectionName} className="mb-space-4">
          <div className="flex items-center justify-between mb-space-3">
            <div>
              <div className="text-caption uppercase text-gray-mid">
                Priorities {section.priorityFrom}–{section.priorityTo}
              </div>
              <div className="text-body font-semibold">{section.sectionName}</div>
            </div>
            <div className="text-right">
              <div className="text-caption uppercase text-gray-mid">Section Total</div>
              <div className="text-body font-semibold">{formatMoney(section.total)}</div>
            </div>
          </div>
          <DataTable
            columns={columns}
            rows={section.rows}
            emptyMessage="No disbursements in this section yet."
            rowKey={(r) => r.disbursementId}
          />
        </Card>
      ))}
    </>
  );
}

function OcGauge({
  ratio,
  threshold,
  active,
}: {
  ratio: number;
  threshold: number;
  active: boolean;
}) {
  // Show ratio relative to a 1.5 ceiling; visualisation only.
  const ceiling = 1.5;
  const pct = Math.min(Math.max(ratio / ceiling, 0), 1) * 100;
  const thresholdPct = Math.min(Math.max(threshold / ceiling, 0), 1) * 100;
  const color = active
    ? "bg-status-error"
    : ratio >= threshold
    ? "bg-status-success"
    : "bg-status-warning";
  return (
    <div>
      <div className="text-caption uppercase text-gray-mid mb-space-1">
        OC Ratio
      </div>
      <div className="text-body font-semibold mb-space-2">
        {ratio.toLocaleString(undefined, { maximumFractionDigits: 4 })}x
      </div>
      <div
        className="relative h-2 w-full rounded-full bg-white-warm"
        aria-label="OC ratio gauge"
      >
        <div
          className={`h-2 rounded-full ${color}`}
          style={{ width: `${pct}%` }}
        />
        <div
          className="absolute top-[-4px] h-4 w-[2px] bg-onyx"
          style={{ left: `${thresholdPct}%` }}
          aria-label={`OC threshold ${threshold}`}
          title={`OC threshold ${threshold}`}
        />
      </div>
      <div className="text-caption text-gray-mid mt-space-1">
        Threshold {threshold.toFixed(2)}x
      </div>
    </div>
  );
}

function ReconciliationPanel({
  poolTotal,
  loansTotal,
  variance,
}: {
  poolTotal: number;
  loansTotal: number;
  variance: number;
}) {
  const ok = Math.abs(variance) < 0.005;
  return (
    <div>
      <div className="text-caption uppercase text-gray-mid mb-space-1">
        Reconciliation
      </div>
      <div className="text-small space-y-space-1">
        <Row label="Pool ledger total" value={formatMoney(poolTotal)} />
        <Row label="Contributing loans (WF2)" value={formatMoney(loansTotal)} />
        <Row
          label="Variance"
          value={
            <span className={ok ? "text-status-success" : "text-status-error"}>
              {formatMoney(variance)} {ok ? "✓" : "✗"}
            </span>
          }
        />
      </div>
      {!ok && (
        <div className="text-caption text-status-error mt-space-1">
          Pool ledger and contributing loans disagree by {formatPct(Math.abs(variance / Math.max(poolTotal, 1)) * 100)}.
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-mid">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
